import { useState, useEffect, useCallback } from 'react';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import type { DashboardAnalytics, MonthlyDataPoint } from '../types';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import {
  DATE_PRESETS,
  DATE_PRESET_LABELS,
  ANALYTICS_CONFIG,
  KPI_LABELS,
  CHART_LABELS,
  type DatePreset,
} from '../constants/analytics';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface DateRange {
  start_date?: string;
  end_date?: string;
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [activePreset, setActivePreset] = useState<DatePreset>(
    ANALYTICS_CONFIG.DEFAULT_PRESET,
  );
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: '',
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await axios.get<unknown, DashboardAnalytics>(
        endpoints.analytics.dashboard(dateRange.start_date, dateRange.end_date),
      );
      setAnalytics(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load dashboard data',
      );
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleDateRangeSubmit = () => {
    if (customDateRange.start && customDateRange.end) {
      setDateRange({
        start_date: customDateRange.start,
        end_date: customDateRange.end,
      });
      setActivePreset(DATE_PRESETS.CUSTOM);
      setShowDatePicker(false);
    }
  };

  const handlePresetRange = (preset: DatePreset) => {
    const today = new Date();
    const year = today.getFullYear();

    setActivePreset(preset);

    switch (preset) {
      case DATE_PRESETS.MTD:
        setDateRange({});
        break;
      case DATE_PRESETS.LAST_30: {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        setDateRange({
          start_date: thirtyDaysAgo.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
        });
        break;
      }
      case DATE_PRESETS.LAST_90: {
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(today.getDate() - 90);
        setDateRange({
          start_date: ninetyDaysAgo.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
        });
        break;
      }
      case DATE_PRESETS.YTD:
        setDateRange({
          start_date: `${year}-01-01`,
          end_date: today.toISOString().split('T')[0],
        });
        break;
      case DATE_PRESETS.ALL:
        setDateRange({
          start_date: ANALYTICS_CONFIG.EARLIEST_DATE,
          end_date: today.toISOString().split('T')[0],
        });
        break;
    }
    setShowDatePicker(false);
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: ANALYTICS_CONFIG.CHART_TOOLTIP.backgroundColor,
        padding: ANALYTICS_CONFIG.CHART_TOOLTIP.padding,
        bodySpacing: ANALYTICS_CONFIG.CHART_TOOLTIP.bodySpacing,
        bodyFont: {
          size: ANALYTICS_CONFIG.CHART_TOOLTIP.bodyFontSize,
        },
        titleFont: {
          size: ANALYTICS_CONFIG.CHART_TOOLTIP.titleFontSize,
          weight: 'normal',
        },
        cornerRadius: ANALYTICS_CONFIG.CHART_TOOLTIP.cornerRadius,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: ANALYTICS_CONFIG.CHART_GRID.xAxisTextColor,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: ANALYTICS_CONFIG.CHART_GRID.gridLineColor,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: ANALYTICS_CONFIG.CHART_GRID.yAxisTextColor,
        },
      },
    },
  };

  const visitorChartData = {
    labels:
      analytics?.monthly_data.visitors.map((d: MonthlyDataPoint) => d.month) ||
      [],
    datasets: [
      {
        label: 'Visitors',
        data:
          analytics?.monthly_data.visitors.map(
            (d: MonthlyDataPoint) => d.count,
          ) || [],
        borderColor: ANALYTICS_CONFIG.CHART_COLORS.VISITORS.border,
        backgroundColor: ANALYTICS_CONFIG.CHART_COLORS.VISITORS.background,
        fill: true,
        tension: ANALYTICS_CONFIG.CHART_STYLING.tension,
        borderWidth: ANALYTICS_CONFIG.CHART_STYLING.borderWidth,
        pointRadius: ANALYTICS_CONFIG.CHART_STYLING.pointRadius,
        pointHoverRadius: ANALYTICS_CONFIG.CHART_STYLING.pointHoverRadius,
        pointBackgroundColor: ANALYTICS_CONFIG.CHART_COLORS.VISITORS.point,
        pointBorderColor: ANALYTICS_CONFIG.CHART_STYLING.pointBorderColor,
        pointBorderWidth: ANALYTICS_CONFIG.CHART_STYLING.pointBorderWidth,
      },
    ],
  };

  const messageChartData = {
    labels:
      analytics?.monthly_data.messages.map((d: MonthlyDataPoint) => d.month) ||
      [],
    datasets: [
      {
        label: 'Bot Messages',
        data:
          analytics?.monthly_data.messages.map(
            (d: MonthlyDataPoint) => d.count,
          ) || [],
        borderColor: ANALYTICS_CONFIG.CHART_COLORS.MESSAGES.border,
        backgroundColor: ANALYTICS_CONFIG.CHART_COLORS.MESSAGES.background,
        fill: true,
        tension: ANALYTICS_CONFIG.CHART_STYLING.tension,
        borderWidth: ANALYTICS_CONFIG.CHART_STYLING.borderWidth,
        pointRadius: ANALYTICS_CONFIG.CHART_STYLING.pointRadius,
        pointHoverRadius: ANALYTICS_CONFIG.CHART_STYLING.pointHoverRadius,
        pointBackgroundColor: ANALYTICS_CONFIG.CHART_COLORS.MESSAGES.point,
        pointBorderColor: ANALYTICS_CONFIG.CHART_STYLING.pointBorderColor,
        pointBorderWidth: ANALYTICS_CONFIG.CHART_STYLING.pointBorderWidth,
      },
    ],
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const getDateRangeText = () => {
    return `${analytics?.date_range.start_date || 'N/A'} to ${analytics?.date_range.end_date || 'N/A'}`;
  };

  if (loading) {
    return (
      <div className="flex h-screen flex-col">
        <div className="border-b border-gray-200 bg-white px-6 py-3">
          <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-gray-500">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen flex-col">
        <div className="border-b border-gray-200 bg-white px-6 py-3">
          <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="border-b border-gray-200 bg-white px-6 py-3">
        <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">Date Range:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handlePresetRange(DATE_PRESETS.MTD)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  activePreset === DATE_PRESETS.MTD
                    ? 'bg-[#c31330] text-white border-[#c31330]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[DATE_PRESETS.MTD]}
              </button>
              <button
                onClick={() => handlePresetRange(DATE_PRESETS.LAST_30)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  activePreset === DATE_PRESETS.LAST_30
                    ? 'bg-[#c31330] text-white border-[#c31330]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[DATE_PRESETS.LAST_30]}
              </button>
              <button
                onClick={() => handlePresetRange(DATE_PRESETS.LAST_90)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  activePreset === DATE_PRESETS.LAST_90
                    ? 'bg-[#c31330] text-white border-[#c31330]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[DATE_PRESETS.LAST_90]}
              </button>
              <button
                onClick={() => handlePresetRange(DATE_PRESETS.YTD)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  activePreset === DATE_PRESETS.YTD
                    ? 'bg-[#c31330] text-white border-[#c31330]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[DATE_PRESETS.YTD]}
              </button>
              <button
                onClick={() => handlePresetRange(DATE_PRESETS.ALL)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  activePreset === DATE_PRESETS.ALL
                    ? 'bg-[#c31330] text-white border-[#c31330]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[DATE_PRESETS.ALL]}
              </button>
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                  activePreset === DATE_PRESETS.CUSTOM
                    ? 'bg-[#c31330] text-white border-[#c31330]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {DATE_PRESET_LABELS[DATE_PRESETS.CUSTOM]}
              </button>
            </div>
          </div>

          {showDatePicker && (
            <div className="mt-3 p-4 bg-gray-50 rounded-lg">
              <div className="flex gap-3 items-end">
                <div className="space-y-1">
                  <label htmlFor="start-date" className="text-sm text-gray-600">
                    Start Date
                  </label>
                  <input
                    id="start-date"
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) =>
                      setCustomDateRange((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                    className="h-9 px-3 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="end-date" className="text-sm text-gray-600">
                    End Date
                  </label>
                  <input
                    id="end-date"
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) =>
                      setCustomDateRange((prev) => ({
                        ...prev,
                        end: e.target.value,
                      }))
                    }
                    className="h-9 px-3 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <button
                  onClick={handleDateRangeSubmit}
                  className="px-3 py-1.5 text-sm rounded-md bg-[#c31330] text-white"
                >
                  Apply
                </button>
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="mt-2 text-sm text-gray-500">
            Showing data for: {getDateRangeText()}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
              {KPI_LABELS.TOTAL_MESSAGES.title}
            </div>
            <div className="text-2xl font-semibold text-gray-900">
              {formatNumber(analytics?.counts.total_messages || 0)}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {KPI_LABELS.TOTAL_MESSAGES.subtitle}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
              {KPI_LABELS.TOTAL_FLAGGED.title}
            </div>
            <div className="text-2xl font-semibold text-gray-900">
              {formatNumber(analytics?.counts.total_flagged || 0)}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {KPI_LABELS.TOTAL_FLAGGED.subtitle}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
              {KPI_LABELS.TOTAL_VISITORS.title}
            </div>
            <div className="text-2xl font-semibold text-gray-900">
              {formatNumber(analytics?.counts.total_visitors || 0)}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              {KPI_LABELS.TOTAL_VISITORS.subtitle}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-900">
                {CHART_LABELS.VISITORS.title}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {CHART_LABELS.VISITORS.subtitle}
              </p>
            </div>
            <div style={{ height: ANALYTICS_CONFIG.CHART_HEIGHT }}>
              {analytics?.monthly_data.visitors.length ? (
                <Line data={visitorChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  No data available for selected period
                </div>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-5 bg-white">
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-900">
                {CHART_LABELS.MESSAGES.title}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {CHART_LABELS.MESSAGES.subtitle}
              </p>
            </div>
            <div style={{ height: ANALYTICS_CONFIG.CHART_HEIGHT }}>
              {analytics?.monthly_data.messages.length ? (
                <Line data={messageChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  No data available for selected period
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
